import React, { useState } from "react";
import TextInput from "../../../components/elements/forms/TextInput";
import SubmitButton from "../../../components/elements/forms/SubmitButton";
import { useNavigate } from "react-router-dom";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { change_password } from "../../../services/api/auth";
import { useAppDispatch } from "../../../store/hooks";

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const [showOld, setShowOld] = useState<"password" | "text">("password");
  const [showNew, setShowNew] = useState<"password" | "text">("password");
  const [showConfirm, setShowConfirm] = useState<"password" | "text">("password");
  const [loading, setLoading] = useState(false);
  
  const [data, setData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const onSubmitHandler = async () => {
    let validate = false;
    const newErrors = { oldPassword: "", newPassword: "", confirmPassword: "" };

    // Validate all fields are filled
    if (!data.oldPassword) {
      validate = true;
      newErrors.oldPassword = "warning";
    }
    if (!data.newPassword) {
      validate = true;
      newErrors.newPassword = "warning";
    }
    if (!data.confirmPassword) {
      validate = true;
      newErrors.confirmPassword = "warning";
    }

    // Validate passwords match
    if (data.newPassword && data.confirmPassword && data.newPassword !== data.confirmPassword) {
      validate = true;
      newErrors.newPassword = "warning";
      newErrors.confirmPassword = "warning";
    }

    // Validate old and new passwords are different
    if (data.oldPassword && data.newPassword && data.oldPassword === data.newPassword) {
      validate = true;
      newErrors.newPassword = "warning";
    }

    setErrors(newErrors);
    if (validate) return;

    setLoading(true);
    const success = await change_password(
      { oldPassword: data.oldPassword, newPassword: data.newPassword },
      dispatch
    );
    
    if (success) {
      // Clear form on success
      setData({ oldPassword: "", newPassword: "", confirmPassword: "" });
    }
    setLoading(false);
  };

  return (
    <div className="w-full">
      <div className="max-w-[1096px] mx-auto px-3">
        <div className="flex border-b border-border text-20 pt-3">
          <div
            onClick={() => navigate(`/settings/profile`)}
            className="pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[400]">Profile</p>
          </div>
          <div
            onClick={() => navigate(`/settings/admin/create`)}
            className="pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[400]">Create Admin</p>
          </div>
          <div
            onClick={() => navigate(`/settings/admin`)}
            className="pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[400]">Admins</p>
          </div>
          <div
            onClick={() => navigate(`/settings/change-password`)}
            className="border-b-[3px] border-secondary pb-1 cursor-pointer"
          >
            <p className="font-[400]">Change Password</p>
          </div>
        </div>
        <div className="rounded-10 bg-white px-4 py-6 w-[100%] mt-5">
          <div className="max-w-[600px] mx-auto">
            <h3 className="text-2xl font-semibold mb-6">Change Password</h3>
            <div className="space-y-4">
              <TextInput
                label="CURRENT PASSWORD"
                placeholder="Enter current password"
                name="oldPassword"
                type={showOld}
                status={errors.oldPassword}
                value={data.oldPassword}
                onChange={onChangeHandler}
                right_icon={
                  showOld === "text" ? (
                    <FaRegEyeSlash
                      size={20}
                      className="cursor-pointer"
                      onClick={() => setShowOld("password")}
                      color="#667185"
                    />
                  ) : (
                    <FaRegEye
                      size={20}
                      className="cursor-pointer"
                      onClick={() => setShowOld("text")}
                      color="#667185"
                    />
                  )
                }
              />
              <TextInput
                label="NEW PASSWORD"
                placeholder="Enter new password"
                name="newPassword"
                type={showNew}
                status={errors.newPassword}
                value={data.newPassword}
                onChange={onChangeHandler}
                right_icon={
                  showNew === "text" ? (
                    <FaRegEyeSlash
                      size={20}
                      className="cursor-pointer"
                      onClick={() => setShowNew("password")}
                      color="#667185"
                    />
                  ) : (
                    <FaRegEye
                      size={20}
                      className="cursor-pointer"
                      onClick={() => setShowNew("text")}
                      color="#667185"
                    />
                  )
                }
              />
              <TextInput
                label="CONFIRM NEW PASSWORD"
                placeholder="Confirm new password"
                name="confirmPassword"
                type={showConfirm}
                status={errors.confirmPassword}
                value={data.confirmPassword}
                onChange={onChangeHandler}
                right_icon={
                  showConfirm === "text" ? (
                    <FaRegEyeSlash
                      size={20}
                      className="cursor-pointer"
                      onClick={() => setShowConfirm("password")}
                      color="#667185"
                    />
                  ) : (
                    <FaRegEye
                      size={20}
                      className="cursor-pointer"
                      onClick={() => setShowConfirm("text")}
                      color="#667185"
                    />
                  )
                }
              />
              {data.newPassword && data.confirmPassword && data.newPassword !== data.confirmPassword && (
                <p className="text-red-500 text-sm">Passwords do not match</p>
              )}
              {data.oldPassword && data.newPassword && data.oldPassword === data.newPassword && (
                <p className="text-red-500 text-sm">New password must be different from current password</p>
              )}
            </div>
            <div className="max-w-[300px] mx-auto mt-6">
              <SubmitButton
                title="Change Password"
                onSubmitHandler={onSubmitHandler}
                loading={loading}
                className="font-[400] text-white md:hover:bg-[white] md:hover:text-secondary hover:border-primary"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
